import React, { useEffect } from 'react';
import { gql, useLazyQuery, useMutation } from '@apollo/client';

import { Button, LoadingEllipses } from '@trqk/components';
import { locale, getStatementSelectionId } from '@trqk/utils';
import {
    useEntities,
    useStatementSelection,
    useToast,
} from '../../../../../hooks';

const GET_TRQK_USER = gql`
    query getTrqkUser($id: ID!) {
        getTrqkUser(id: $id) {
            id
            entities_remaining
        }
    }
`;

const DELETE_TRQK_ENTITY = gql`
    mutation deleteTrqkEntity($deletetrqkentityinput: DeleteTrqkEntityInput!) {
        deleteTrqkEntity(input: $deletetrqkentityinput) {
            id
        }
    }
`;

/**
 * @typedef {DeleteEntityModalInput}
 * @property {string} entityId an entity ID
 * @property {stringOrNull} userId the current user's ID
 */
interface DeleteEntityModalInput {
    deleteTableRowByIndex: DeleteTableRowByIndex;
    entityId: string;
    index: number;
    userId: Maybe<string>;
}

/**
 * Contents for a modal that allows the user to delete an entity.
 * @param props {ModalContentProps}
 */
export const DeleteEntityModalContent = ({
    remove,
    input,
}: ModalContentProps<DeleteEntityModalInput>): JSX.Element => {
    const deleteTableRowByIndex = input?.deleteTableRowByIndex ?? null;
    const entityId = input?.entityId ?? null;
    const index = input?.index ?? null;
    const userId = input?.userId ?? null;
    const toast = useToast();
    const {
        sendEntitySelectionEvent,
        entities,
        // refetchActiveEntitiesLater,
    } = useEntities();
    const {
        statementSelectionProps: { sendStatementSelectionEvent },
    } = useStatementSelection();
    const [getUser, getUserQuery] = useLazyQuery(GET_TRQK_USER);
    const [deleteTrqkEntity, deleteTrqkEntityQuery] = useMutation(
        DELETE_TRQK_ENTITY,
        {
            onCompleted: data => {
                const deletedEntityId = data?.deleteTrqkEntity?.id;

                if (
                    data.errors ||
                    (typeof deletedEntityId === 'string' &&
                        deletedEntityId === entityId)
                ) {
                    // Delete the entity from the entitySelectionMachine
                    sendEntitySelectionEvent({
                        type: 'DELETE_ENTITY',
                        value: {
                            id: deletedEntityId,
                        },
                    });

                    toast.add({
                        kind: 'info-banner',
                        content:
                            locale.enUS.ACCOUNT.TOASTS.DELETE_ENTITY_SUCCEEDED,
                    });

                    if (deleteTableRowByIndex && index) {
                        deleteTableRowByIndex(index);
                    }

                    // Ensure that the statement selection context for the deleted entity is deleted
                    // from the StatementSelectionMachine
                    const deletedEntity = entities.find(
                        ({ id }: TrqkEntity): boolean => id === entityId,
                    );

                    if (deletedEntity && userId != null) {
                        const selectionId = getStatementSelectionId({
                            userId,
                            entityId: deletedEntity.id,
                            prefix: '',
                        });

                        sendStatementSelectionEvent({
                            type: 'DELETE_SELECTION_MACHINE',
                            value: {
                                id: selectionId,
                            },
                        });
                    }

                    remove({
                        canceled: false,
                        success: true,
                    });
                } else {
                    toast.add({
                        kind: 'warning-banner',
                        content:
                            locale.enUS.ACCOUNT.TOASTS.DELETE_ENTITY_FAILED,
                    });

                    remove({
                        canceled: false,
                        success: false,
                    });
                }
            },
            onError: () => {
                toast.add({
                    kind: 'warning-banner',
                    content: locale.enUS.ACCOUNT.TOASTS.DELETE_ENTITY_FAILED,
                });

                remove({
                    canceled: false,
                    success: false,
                });
            },
            update: (cache, result) => {
                const deletedTrqkEntity =
                    result?.data?.deleteTrqkEntity ?? null;

                if (deletedTrqkEntity !== null) {
                    cache.evict({
                        id: cache.identify(deletedTrqkEntity),
                    });
                }
            },
        },
    );

    useEffect((): void => {
        if (userId !== null) {
            getUser({
                variables: {
                    id: userId,
                },
            });
        }
    }, [getUser, userId]);

    const entitiesRemaining =
        getUserQuery?.data?.getTrqkUser?.entities_remaining ?? null;

    return (
        <div
            className="px-12 py-10 bg-white rounded-lg border-l-4 border-quinary-500"
            style={{ width: 450 }}
        >
            {/*
                // TODO: confirm that the API is returning a number for trqkUser.entities_remaining
                // https://app.asana.com/0/1161035310203426/1190084540645105
            */}
            {entitiesRemaining !== null && (
                <>
                    <p>
                        {
                            locale.enUS.ACCOUNT.MODALS.CONFIRM_DELETE_ENTITY
                                .PART_1
                        }
                        &nbsp;
                        {entitiesRemaining}&nbsp;
                        {
                            locale.enUS.ACCOUNT.MODALS.CONFIRM_DELETE_ENTITY
                                .PART_2
                        }
                    </p>
                    <br />
                </>
            )}
            <p>{locale.enUS.ACCOUNT.MODALS.CONFIRM_DELETE_ENTITY.PART_3}</p>

            <div className="flex justify-end mt-6">
                <Button
                    color="danger-tertiary-light-bg"
                    autoWidth={true}
                    addClasses="mr-4"
                    handleClick={(): void => {
                        remove({
                            canceled: true,
                            success: false,
                        });
                    }}
                >
                    <div className="text-center mx-3">
                        {
                            locale.enUS.ACCOUNT.MODALS.CONFIRM_DELETE_ENTITY
                                .BUTTONS.CANCEL
                        }
                    </div>
                </Button>
                <Button
                    autoWidth={true}
                    color="danger-primary-light-bg"
                    handleClick={(): void => {
                        deleteTrqkEntity({
                            variables: {
                                deletetrqkentityinput: {
                                    user_id: userId,
                                    entity_id: entityId,
                                },
                            },
                        });
                    }}
                >
                    <div className="flex justify-center" style={{ width: 150 }}>
                        {!deleteTrqkEntityQuery.loading && (
                            <>
                                {
                                    locale.enUS.ACCOUNT.MODALS
                                        .CONFIRM_DELETE_ENTITY.BUTTONS
                                        .DELETE_ENTITY
                                }
                            </>
                        )}
                        {deleteTrqkEntityQuery.loading && (
                            <LoadingEllipses width={40} />
                        )}
                    </div>
                </Button>
            </div>
        </div>
    );
};
